/**
 * Custom multi-select with inline search and tag chips. Replaces SlimSelect — no external dependencies.
 * Wraps a native <select multiple> element (hidden, stays in DOM for form submission).
 */

const CHECK_SVG = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 6.5L4.5 9L10 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const TIMES_SVG = `<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7M7 1L1 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;

function mk<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  return e;
}

export interface MultipleSearchSelectOptions {
  placeholder?: string;
  searchPlaceholder?: string;
  openPosition?: "auto" | "up" | "down";
  /** 'top': 검색창을 드롭다운 상단에 고정. 기본(undefined)은 trigger 내부 인라인 검색 */
  searchPosition?: "top";
  addable?: boolean | ((value: string) => string | false);
  onChange?: (values: string[]) => void;
  /** 리치 모드: 옵션·칩에 썸네일 + 제목 + 메타(날짜) 표시. 미지정 시 option[data-thumb] 자동감지. */
  rich?: boolean;
}

interface Item { value: string; label: string; disabled: boolean; custom?: boolean; thumb?: string; meta?: string; }

let _uid = 0;

export class MultipleSearchSelect {
  private readonly el: HTMLSelectElement;
  private readonly opts: MultipleSearchSelectOptions;
  private container!: HTMLDivElement;
  private trigger!: HTMLDivElement;
  private tagsEl!: HTMLDivElement;
  private searchInput!: HTMLInputElement;
  private dropdown!: HTMLDivElement;
  private listEl!: HTMLUListElement;
  private allItems: Item[] = [];
  private selected = new Set<string>();
  private isOpen = false;
  private activeIdx = -1;
  private rich = false;
  private readonly uid: string;
  private get _searchTop(): boolean { return this.opts.searchPosition === "top"; }

  constructor(target: string | HTMLSelectElement, opts: MultipleSearchSelectOptions = {}) {
    const el = typeof target === "string"
      ? document.querySelector<HTMLSelectElement>(target)
      : target;
    if (!el) throw new Error("MultipleSearchSelect: element not found");
    this.el = el;
    this.opts = opts;
    this.uid = `mcs${++_uid}`;
  }

  init(): this {
    if (this.el.dataset.mcsInited) return this;
    this._parse();
    this._build();
    this._wire();
    this.el.dataset.mcsInited = "1";
    return this;
  }

  getValues(): string[] { return [...this.selected]; }

  setValues(values: string[]): void {
    this.selected = new Set(values);
    this._syncNative();
    this._renderTags();
    this._buildList(this._filtered());
  }

  destroy(): void {
    document.removeEventListener("click", this._onDocClick, true);
    window.removeEventListener("scroll", this._onReposition, true);
    window.removeEventListener("resize", this._onReposition);
    this.dropdown?.remove();
    this.container?.remove();
    this.el.style.display = "";
    delete this.el.dataset.mcsInited;
  }

  private _parse(): void {
    this.allItems = Array.from(this.el.options)
      .filter(o => !(o.value === "" && (o.hasAttribute("data-placeholder") || o.text.trim() === "")))
      .map(o => ({
        value: o.value,
        label: o.text.trim(),
        disabled: o.disabled,
        thumb: o.dataset.thumb,
        meta: o.dataset.meta,
      }));
    Array.from(this.el.options)
      .filter(o => o.selected && o.value)
      .forEach(o => this.selected.add(o.value));
    // 리치 모드: 명시 옵션 > select[data-rich] > 옵션 중 data-thumb 존재 자동감지.
    this.rich = this.opts.rich
      ?? (this.el.dataset.rich === "1" || this.allItems.some(i => i.thumb !== undefined));
  }

  /** 리치 썸네일 엘리먼트 — URL 있으면 <img>, 없으면 placeholder 박스. */
  private _thumbEl(thumb?: string): HTMLElement {
    if (thumb) {
      const img = mk("img", { class: "mcs-thumb", src: thumb, alt: "", loading: "lazy" });
      return img;
    }
    return mk("span", { class: "mcs-thumb mcs-thumb--empty", "aria-hidden": "true" });
  }

  private _ph(): string {
    return this.opts.placeholder ?? this.el.dataset.placeholder ?? "선택하세요";
  }

  private _build(): void {
    this.el.style.display = "none";
    this.container = mk("div", {
      class: "mcs-wrap"
        + (this._searchTop ? " mcs-wrap--search-top" : "")
        + (this.rich ? " mcs-wrap--rich" : ""),
    });

    // Trigger (tag chips + inline search when not searchTop)
    this.trigger = mk("div", {
      class: "mcs-trigger",
      role: "combobox", "aria-haspopup": "listbox",
      "aria-expanded": "false", "aria-controls": `${this.uid}-lb`,
      tabindex: "0",
    }) as HTMLDivElement;
    this.tagsEl = mk("div", { class: "mcs-tags" }) as HTMLDivElement;
    this.searchInput = mk("input", {
      type: "text", class: "mcs-search",
      autocomplete: "off", "aria-label": "검색",
    }) as HTMLInputElement;
    if (this._searchTop) {
      this.trigger.append(this.tagsEl);
    } else {
      this.trigger.append(this.tagsEl, this.searchInput);
    }
    this._renderTags();

    // Dropdown
    this.dropdown = mk("div", {
      class: "mcs-dropdown", id: `${this.uid}-lb`,
      role: "listbox", "aria-multiselectable": "true",
    }) as HTMLDivElement;
    this.dropdown.hidden = true;

    if (this._searchTop) {
      const searchWrap = mk("div", { class: "mcs-search-bar" }) as HTMLDivElement;
      const searchIcon = mk("span", { class: "mcs-search-bar__icon", "aria-hidden": "true" }) as HTMLSpanElement;
      searchIcon.innerHTML = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="6" cy="6" r="4.5" stroke="currentColor" stroke-width="1.4"/><path d="M10 10L12.5 12.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>`;
      this.searchInput.setAttribute("placeholder", this.opts.searchPlaceholder ?? "검색...");
      searchWrap.append(searchIcon, this.searchInput);
      this.dropdown.appendChild(searchWrap);
    }

    this.listEl = mk("ul", { class: "mcs-list", "data-lenis-prevent": "" }) as HTMLUListElement;
    this.dropdown.appendChild(this.listEl);
    this._buildList(this.allItems);

    this.container.append(this.trigger, this.dropdown);
    this.el.insertAdjacentElement("afterend", this.container);
  }

  private _renderTags(): void {
    this.tagsEl.innerHTML = "";
    this.selected.forEach(v => {
      const item = this.allItems.find(i => i.value === v);
      if (!item) return;
      const btn = mk("button", {
        type: "button", class: "mcs-tag__remove",
        "aria-label": `${item.label} 제거`,
        "data-value": v, tabindex: "-1",
      }) as HTMLButtonElement;
      btn.innerHTML = TIMES_SVG;

      if (this.rich) {
        const tag = mk("span", { class: "mcs-tag mcs-tag--rich" });
        const body = mk("span", { class: "mcs-tag__body" });
        const title = mk("span", { class: "mcs-tag__title" });
        title.textContent = item.label;
        body.appendChild(title);
        if (item.meta) {
          const meta = mk("span", { class: "mcs-tag__meta" });
          meta.textContent = item.meta;
          body.appendChild(meta);
        }
        tag.append(this._thumbEl(item.thumb), body, btn);
        this.tagsEl.appendChild(tag);
        return;
      }

      const tag = mk("span", { class: "mcs-tag" });
      const lbl = mk("span", { class: "mcs-tag__label" });
      lbl.textContent = item.label;
      tag.append(lbl, btn);
      this.tagsEl.appendChild(tag);
    });
    if (!this._searchTop) {
      this.searchInput.placeholder = this.selected.size === 0 ? this._ph() : "";
    }
    // searchTop 모드: trigger에 placeholder 텍스트 표시
    if (this._searchTop) {
      let phEl = this.trigger.querySelector<HTMLSpanElement>(".mcs-trigger-placeholder");
      if (this.selected.size === 0) {
        if (!phEl) {
          phEl = mk("span", { class: "mcs-trigger-placeholder" }) as HTMLSpanElement;
          this.trigger.appendChild(phEl);
        }
        phEl.textContent = this._ph();
      } else {
        phEl?.remove();
      }
    }
  }

  private _buildList(items: Item[]): void {
    this.listEl.innerHTML = "";
    this.activeIdx = -1;
    const q = this.searchInput.value.trim();
    const showAdd = this.opts.addable && q && !this.allItems.find(i => i.label === q);

    if (!items.length && !showAdd) {
      const li = mk("li", { class: "mcs-option mcs-option--empty" });
      li.textContent = "결과 없음";
      this.listEl.appendChild(li);
      return;
    }

    if (showAdd) {
      const li = mk("li", {
        class: "mcs-option mcs-option--addable",
        role: "option", "data-value": q, "aria-selected": "false",
      });
      const icon = mk("span", { class: "mcs-option__add-icon", "aria-hidden": "true" });
      icon.textContent = "+";
      const lbl = mk("span", { class: "mcs-option__label" });
      lbl.textContent = `"${q}" 추가`;
      li.append(icon, lbl);
      this.listEl.appendChild(li);
    }

    items.forEach(item => {
      const sel = this.selected.has(item.value);
      const cls = ["mcs-option", sel && "mcs-option--selected", item.disabled && "mcs-option--disabled"]
        .filter(Boolean).join(" ");
      const li = mk("li", {
        class: cls, role: "option", "data-value": item.value,
        "aria-selected": String(sel),
        ...(item.disabled ? { "aria-disabled": "true" } : {}),
      });
      const chk = mk("span", { class: "mcs-option__check", "aria-hidden": "true" });
      chk.innerHTML = sel ? CHECK_SVG : "";

      if (this.rich) {
        const body = mk("span", { class: "mcs-option__body" });
        const title = mk("span", { class: "mcs-option__title" });
        title.textContent = item.label;
        body.appendChild(title);
        if (item.meta) {
          const meta = mk("span", { class: "mcs-option__meta" });
          meta.textContent = item.meta;
          body.appendChild(meta);
        }
        li.append(chk, this._thumbEl(item.thumb), body);
        this.listEl.appendChild(li);
        return;
      }

      const lbl = mk("span", { class: "mcs-option__label" });
      lbl.textContent = item.label;
      li.append(chk, lbl);
      this.listEl.appendChild(li);
    });
  }

  private _filtered(): Item[] {
    const q = this.searchInput.value.toLowerCase();
    return q ? this.allItems.filter(i => i.label.toLowerCase().includes(q)) : this.allItems;
  }

  private _wire(): void {
    this.trigger.addEventListener("click", e => {
      if ((e.target as Element).closest(".mcs-tag__remove")) return;
      if (!this.isOpen) this._open(); else this.searchInput.focus();
    });
    this.trigger.addEventListener("keydown", e => this._onTriggerKey(e));
    this.tagsEl.addEventListener("click", e => {
      const btn = (e.target as Element).closest<HTMLButtonElement>(".mcs-tag__remove");
      if (btn) { e.stopPropagation(); this._deselect(btn.dataset.value ?? ""); }
    });
    this.searchInput.addEventListener("input", () => this._buildList(this._filtered()));
    this.searchInput.addEventListener("keydown", e => this._onSearchKey(e));
    this.listEl.addEventListener("click", e => {
      const li = (e.target as Element).closest<HTMLElement>(".mcs-option");
      if (!li || li.classList.contains("mcs-option--disabled") || li.classList.contains("mcs-option--empty")) return;
      if (li.classList.contains("mcs-option--addable")) this._addCustom(li.dataset.value ?? "");
      else this._toggle(li.dataset.value ?? "");
    });
    document.addEventListener("click", this._onDocClick, true);
  }

  private _open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    // 포탈: overflow/clip 조상을 벗어나도록 body 로 옮기고 position:fixed 로 배치.
    document.body.appendChild(this.dropdown);
    this.dropdown.classList.add("mcs-dropdown--floating");
    this.dropdown.hidden = false;
    this.trigger.setAttribute("aria-expanded", "true");
    this.trigger.classList.add("mcs-trigger--open");
    this._buildList(this._filtered());
    this._position();
    window.addEventListener("scroll", this._onReposition, true);
    window.addEventListener("resize", this._onReposition);
    requestAnimationFrame(() => { this.searchInput.focus(); this._position(); });
    if (this._searchTop) this.searchInput.value = "";
  }

  private _close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.dropdown.hidden = true;
    window.removeEventListener("scroll", this._onReposition, true);
    window.removeEventListener("resize", this._onReposition);
    this.dropdown.classList.remove("mcs-dropdown--floating", "mcs-dropdown--up");
    const s = this.dropdown.style;
    s.position = s.top = s.left = s.bottom = s.minWidth = s.maxWidth = s.zIndex = "";
    this.container.appendChild(this.dropdown);
    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.classList.remove("mcs-trigger--open");
    this.searchInput.value = "";
    if (this._searchTop) this._buildList(this.allItems);
    this.activeIdx = -1;
    this._clearActive();
  }

  private _onReposition = (): void => {
    if (this.isOpen) this._position();
  };

  private _position(): void {
    const r = this.trigger.getBoundingClientRect();
    const dd = this.dropdown;
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    dd.style.position = "fixed";
    // app-modal-backdrop(z-index:20000) 위로 — 모달 안 select 도 정상 표시.
    dd.style.zIndex = "20050";
    dd.style.minWidth = `${Math.round(r.width)}px`;
    dd.style.maxWidth = `${Math.round(vw - 16)}px`;
    const ddW = Math.max(dd.offsetWidth, r.width);
    const ddH = dd.offsetHeight;
    const forced = this.opts.openPosition ?? "auto";
    const spaceBelow = vh - r.bottom;
    const openUp = forced === "up"
      ? true
      : forced === "down"
        ? false
        : spaceBelow < Math.min(ddH + 8, 260) && r.top > spaceBelow;
    dd.classList.toggle("mcs-dropdown--up", openUp);
    let left = r.left;
    if (left + ddW > vw - 8) left = Math.max(8, vw - 8 - ddW);
    dd.style.left = `${Math.round(left)}px`;
    if (openUp) {
      dd.style.top = "auto";
      dd.style.bottom = `${Math.round(vh - r.top + 4)}px`;
    } else {
      dd.style.bottom = "auto";
      dd.style.top = `${Math.round(r.bottom + 4)}px`;
    }
  }

  private _toggle(value: string): void {
    if (this.selected.has(value)) this._deselect(value);
    else {
      const item = this.allItems.find(i => i.value === value);
      if (!item || item.disabled) return;
      this.selected.add(value);
      this._afterChange();
    }
  }

  private _deselect(value: string): void {
    this.selected.delete(value);
    this._afterChange();
  }

  private _addCustom(raw: string): void {
    const value = typeof this.opts.addable === "function"
      ? (this.opts.addable(raw) || raw.trim())
      : raw.trim();
    if (!value) return;
    if (!this.allItems.find(i => i.value === value)) {
      this.allItems.push({ value, label: value, disabled: false, custom: true });
      const opt = document.createElement("option");
      opt.value = value; opt.text = value;
      this.el.appendChild(opt);
    }
    this.selected.add(value);
    this.searchInput.value = "";
    this._afterChange();
  }

  private _afterChange(): void {
    this._syncNative();
    this._renderTags();
    this._buildList(this._filtered());
    this.opts.onChange?.([...this.selected]);
    this.el.dispatchEvent(new Event("change", { bubbles: true }));
  }

  private _syncNative(): void {
    Array.from(this.el.options).forEach(o => { o.selected = this.selected.has(o.value); });
  }

  private _onTriggerKey(e: KeyboardEvent): void {
    if (e.key === "Escape") { this._close(); this.trigger.focus(); return; }
    if ([" ", "ArrowDown", "ArrowUp"].includes(e.key) && !this.searchInput.value) {
      e.preventDefault();
      if (!this.isOpen) this._open();
      else if (e.key === "ArrowDown") this._moveActive(1);
      else if (e.key === "ArrowUp") this._moveActive(-1);
    }
  }

  private _onSearchKey(e: KeyboardEvent): void {
    if (e.key === "Escape") { this._close(); this.trigger.focus(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); this._moveActive(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); this._moveActive(-1); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const active = this.listEl.querySelector<HTMLElement>(".mcs-option--active");
      if (active) {
        if (active.classList.contains("mcs-option--addable")) this._addCustom(active.dataset.value ?? "");
        else this._toggle(active.dataset.value ?? "");
      } else if (this.opts.addable && this.searchInput.value.trim()) {
        this._addCustom(this.searchInput.value.trim());
      }
    } else if (e.key === "Backspace" && !this.searchInput.value) {
      const last = [...this.selected].pop();
      if (last) this._deselect(last);
    }
  }

  private _moveActive(dir: 1 | -1): void {
    const items = [...this.listEl.querySelectorAll<HTMLElement>(".mcs-option:not(.mcs-option--disabled):not(.mcs-option--empty)")];
    if (!items.length) return;
    this._clearActive();
    this.activeIdx = Math.max(0, Math.min(items.length - 1, this.activeIdx + dir));
    items[this.activeIdx].classList.add("mcs-option--active");
    items[this.activeIdx].scrollIntoView({ block: "nearest" });
  }

  private _clearActive(): void {
    this.listEl.querySelectorAll(".mcs-option--active").forEach(e => e.classList.remove("mcs-option--active"));
  }

  private _onDocClick = (e: MouseEvent): void => {
    // dropdown 은 열려 있을 때 body 로 포탈되므로 함께 검사한다.
    const target = e.target as Node;
    if (!this.container.contains(target) && !this.dropdown.contains(target)) this._close();
  };
}
